import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Screenshot,
  Section,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const YourShifts = () => (
  <Page
    title="Your Shifts"
    intro="Everything about your own work lives in two places: Available
    Shifts, where you find and apply for open work, and My Shifts, where your
    confirmed shifts, acknowledgements, and applications live. This guide also
    covers saying you cannot attend, swap requests, shift reminders, putting
    your shifts in your own calendar, and clocking in and out."
  >
    <Section title="Finding shifts on Available Shifts">
      <p>
        <DocLink text="Available Shifts" url="/available-shifts" /> lists the
        open event, patient transport, and rota shifts your managers have
        advertised, grouped by day. Summary tiles at the top show how many
        shifts are open to you, how many you have applied for, how many are
        swap requests from colleagues, and when the next advertised shift
        starts.
      </p>
      <Bullets>
        <Bullet>
          <strong>Filter by shift type</strong> — Events, PTS, or Rota — and
          by clinical role. Each filter shows how many shifts it contains, and
          a note tells you when shifts are hidden by your role filter.
        </Bullet>
        <Bullet>
          <strong>One row per identical vacancy</strong> — when several people
          are needed for the same role, date, and time, you see a single
          listing with a count like "3 open · 1/4 filled" rather than a
          separate row for each slot. Apply to the listing; AmbuNet works out
          which underlying vacancy your application lands on.
        </Bullet>
        <Bullet>
          <strong>TBC</strong> marks shifts on events that haven't been
          confirmed yet — you can still apply, but the shift is provisional.
        </Bullet>
        <Bullet>
          <strong>Swap request</strong> marks a shift a colleague has offered
          out because they can no longer work it. A shift you have offered
          yourself shows as <strong>Your swap offer</strong>.
        </Bullet>
      </Bullets>
      <p>
        Open a shift to see its full details: date, times and duration,
        clinical role, the event or PTS duty it belongs to, the location, any
        shift note — and the pay: the rate (per hour or per shift), any fixed
        bonuses such as a meal allowance, and paid travel time where your
        organisation has configured them. For a PTS shift you also see the
        duty, the vehicle allocated so far, and who the work is for — never
        any patient details before the shift is yours.
      </p>
    </Section>

    <Section title="Applying, and what happens next">
      <p>
        Select <strong>Request Shift</strong> to apply. The popup warns you
        first if you are already assigned to, or have already applied for,
        something that overlaps. While an application is pending you can
        withdraw it at any time with <strong>Cancel Application</strong>.
      </p>
      <p>
        A manager then reviews everyone who applied. Your applications are
        listed at the bottom of{' '}
        <DocLink text="My Shifts" url="/my-shifts" /> as{' '}
        <strong>Pending</strong>, <strong>Accepted</strong>, or{' '}
        <strong>Rejected</strong>. You're notified when an application is
        accepted — and when one is rejected, unless your organisation has
        switched rejection notices off. An accepted application makes the
        shift yours and it moves into your upcoming shifts.
      </p>
      <Info title="Sometimes you can't apply">
        Depending on your organisation's settings, you may be blocked from
        requesting shifts while your mandatory training is expired or an
        occupational health screening is outstanding. Available Shifts tells
        you when this applies — you can still browse the board and withdraw
        existing applications.
      </Info>
    </Section>

    <Section title="My Shifts — your confirmed work">
      <p>
        <DocLink text="My Shifts" url="/my-shifts" /> shows your upcoming
        shifts, shifts waiting for your acknowledgement, your applications,
        and your recent shifts. Recent shifts carry a{' '}
        <strong>Verified</strong> or <strong>Pending Verification</strong>{' '}
        chip — verification is the confirmation that the shift was worked as
        recorded, and it has to happen before the shift can be paid (see{' '}
        <DocLink docsPage url="/crew/your-pay" text="Your Pay" />
        ).
      </p>
    </Section>

    <Section title="Acknowledging a shift">
      <p>
        When a manager gives you a shift directly — rather than you applying
        for it — the shift asks for your acknowledgement, so your managers
        know you have actually seen it. These shifts show an{' '}
        <strong>Acknowledgement Required</strong> chip on My Shifts, with two
        buttons: <strong>Acknowledge</strong> to confirm you've seen it and
        will be there, or <strong>Cannot Attend</strong> if you can't work it.
        On-call shifts never ask for acknowledgement.
      </p>
    </Section>

    <Section title="Cannot Attend, and offering a shift for a swap">
      <p>
        Choosing <strong>Cannot Attend</strong> asks for a reason, tells your
        managers, and puts the shift up on Available Shifts for colleagues to
        apply for.
      </p>
      <Warning title="You stay assigned until a manager reassigns it">
        Saying you cannot attend does not remove you from the shift — you
        remain assigned and responsible for it until a manager accepts a
        colleague's application or reassigns it. If your plans change back,
        use <strong>I can attend after all</strong> on the shift: the swap
        offer is withdrawn and any colleagues who applied are told it is no
        longer available.
      </Warning>
      <p>
        You can also offer a confirmed future shift out without declining it:
        open the shift from My Shifts and choose{' '}
        <strong>Offer Shift as Available</strong>. It appears on colleagues'
        Available Shifts as a swap request, your managers are told, and you
        can cancel the swap request from the same place while it is still
        yours.
      </p>
    </Section>

    <Section title="Reminders and your own calendar">
      <p>
        Each morning AmbuNet sends you a reminder — by push notification and
        email, following your notification preferences — listing any of your
        shifts that start within the next 48 hours.
      </p>
      <p>
        To see your shifts alongside everything else in your life, open{' '}
        <DocLink text="My Account" url="/my-account" /> →{' '}
        <strong>Calendar</strong> and create a personal calendar feed. Google
        Calendar, Outlook, Microsoft 365, and Apple Calendar can all
        subscribe to the link, and your upcoming shifts then appear and update
        automatically. Create a separate link per device or app — each one
        can be copied, reset, or revoked on its own, so a lost phone only
        means revoking that one feed.
      </p>
    </Section>

    <Section title="Clocking in and out">
      <p>
        On the day, your shift appears as the active-shift card on the Home
        screen, where you clock in and out. The clock-in button unlocks
        shortly before your start time — 15 minutes before by default,
        though your organisation can change the window.
      </p>
      <Screenshot url="getting-started/home-dashboard.png">
        The Home screen — the active-shift card is where you clock in and
        out on the day.
      </Screenshot>
      <p>
        Clocking out on time finishes the shift cleanly. If you clock out
        late, you're asked for a reason (patient handover delay, journey
        overran, and so on) and the extra time is recorded automatically as
        an overtime claim for your managers to review — see{' '}
        <DocLink docsPage url="/crew/your-pay" text="Your Pay" />. For the
        rest of the Home screen, see{' '}
        <DocLink
          docsPage
          url="/getting-started/home-screen"
          text="Home Screen"
        />
        .
      </p>
    </Section>

    <Section title="Who can do this?">
      <Info title="Who can do this?">
        Any signed-in staff member. Available Shifts only shows work your
        managers have advertised, and your organisation can block shift
        requests while mandatory training is expired or an occupational
        health screening is outstanding. Advertising shifts and reviewing
        applications is a manager's job — managing shifts? See{' '}
        <DocLink
          docsPage
          url="/employees-hr/shifts"
          text="Shifts and Staff Work"
        />
        .
      </Info>
    </Section>
  </Page>
)

export default YourShifts
